import React, { useMemo } from 'react';
import { useControllerEvidenceCoverage } from '../../../hooks/analytics/useControllerReports.ts';
import { formatPercent, ProgressBar, WidgetCard } from './shared.tsx';

/** Report #28 — Evidence Coverage Map. Progress bar per control: % of tested samples with evidence attached. */
export const EvidenceCoverageBars: React.FC<{ controlIdFilter?: Set<string> | null }> = ({ controlIdFilter }) => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useControllerEvidenceCoverage();

  const sorted = useMemo(() => {
    const base = controlIdFilter ? (data ?? []).filter((r) => controlIdFilter.has(r.control_id)) : data ?? [];
    return [...base].sort((a, b) => (a.coverage_pct ?? 0) - (b.coverage_pct ?? 0));
  }, [data, controlIdFilter]);

  return (
    <WidgetCard
      title="Evidence Coverage Map"
      description="% of tested samples with evidence attached (AI-sourced, manual, or missing)"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load evidence coverage.'}
      isEmpty={!isLoading && !isError && sorted.length === 0}
      emptyMessage="No sampled controls yet."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <ul className="analytics-scroll space-y-3 max-h-96 overflow-y-auto">
        {sorted.map((row) => {
          const pct = row.coverage_pct ?? 0;
          const colorClass = pct >= 90 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
          return (
            <li key={row.control_id}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="font-medium text-gray-800">{row.control_id}</span>
                <span className="text-gray-600">
                  {formatPercent(row.coverage_pct)} ({row.covered}/{row.samples})
                </span>
              </div>
              <ProgressBar pct={pct} colorClass={colorClass} />
            </li>
          );
        })}
      </ul>
    </WidgetCard>
  );
};

export default EvidenceCoverageBars;
